<template>
  <div class="login-page-wrapper relative min-h-screen overflow-hidden">
    <!-- Dynamic Form Styling -->
    <component :is="'style'" v-if="dynamicTextStyles">{{
      dynamicTextStyles
    }}</component>

    <!-- Loading overlay (requisição) -->
    <div v-if="!requisicaoFinalizada" class="loading-overlay">
      <div class="spinner"></div>
    </div>

    <!-- Loading overlay (background config) -->
    <div
      v-if="!isBackgroundReady"
      class="fixed inset-0 bg-black z-50 flex items-center justify-center"
    >
      <div class="flex flex-col items-center gap-4">
        <div
          class="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin"
        ></div>
        <p class="text-white/80 text-sm">Carregando...</p>
      </div>
    </div>

    <!-- Solid gradient background -->
    <div class="auth-background" :style="customBackgroundStyle"></div>

    <!-- Subtle overlay for depth -->
    <div class="auth-overlay"></div>

    <!-- Content layer -->
    <div
      class="login-glass relative z-10 min-h-screen flex flex-col px-6 py-10 pb-6 sm:py-12 sm:pb-8 lg:px-0 lg:py-0"
    >
      <!-- TOP: Theme toggle only -->
      <div
        class="flex items-center justify-end w-full max-w-sm mx-auto lg:max-w-none lg:px-12 lg:pt-8 lg:pb-4"
      >
        <div class="flex items-center gap-2">
          <BackgroundCustomizer
            ref="backgroundCustomizer"
            :nomeEmpresa="nomeEmpresa"
            :showControls="showBackgroundCustomizer"
            @background-change="handleBackgroundChange"
            @button-color-change="handleButtonColorChange"
            @form-styling-change="handleFormStyling"
          />

          <button
            @click="handleThemeClick"
            class="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="Toggle theme"
            :aria-pressed="isDark"
          >
            <svg
              v-if="!isDark"
              class="w-5 h-5 text-yellow-300"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
              />
            </svg>
            <svg
              v-else
              class="w-5 h-5 text-slate-300"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"
              />
            </svg>
          </button>
          <!-- Secret input for editor mode activation -->
          <input
            v-if="showSecretInput"
            v-model="secretInput"
            @input="checkSecretCode"
            ref="secretInput"
            type="text"
            class="fixed -left-full opacity-0 w-0 h-0"
            autocomplete="off"
          />
        </div>
      </div>

      <!-- CENTER -->
      <div
        class="flex-1 flex flex-col justify-center w-full pb-8"
        :class="parentContainerClass"
        :style="parentContainerStyle"
      >
        <!-- ESTADO TOKEN -->
        <div
          v-if="openToken"
          class="space-y-6 form-container"
          :class="[formContainerClass, childContainerWidthClass]"
          :style="formContainerStyle"
        >
          <!-- Botão voltar -->
          <button
            @click="voltar()"
            class="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Voltar
          </button>

          <!-- Ícone cadeado -->
          <div class="flex justify-center">
            <div
              class="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center"
            >
              <svg
                class="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
          </div>

          <!-- Título -->
          <h2 class="text-2xl font-semibold text-center text-white">
            Chave de acesso
          </h2>

          <!-- Descrição -->
          <p class="text-sm text-center text-white/60">
            {{ textoDescricaoToken }}
          </p>

          <!-- Formulário token -->
          <form @submit.prevent="sendRegisterWithToken" class="space-y-4">
            <div>
              <label class="block text-sm font-medium mb-2">
                Chave de acesso
              </label>
              <Password
                v-model="token"
                :feedback="false"
                toggleMask
                autocomplete="off"
                @click="clear"
                :class="{ 'border-red-500': erro }"
              />

              <p v-if="messageError" class="erro mt-2">{{ messageError }}</p>
            </div>

            <Button
              type="submit"
              :loading="disableBtnConcluir"
              :disabled="disableBtnConcluir"
              label="Concluir"
              class="w-full btn-dynamic-color"
            />
          </form>
        </div>
        <!-- ESTADO FORMULÁRIO -->
        <div
          v-if="!openToken"
          class="form-container mt-8 lg:mt-0"
          :class="[formContainerClass, childContainerWidthClass]"
          :style="formContainerStyle"
        >
          <!-- Logo (mobile e desktop) -->
          <div class="flex justify-center mb-6 lg:mb-10">
            <div
              :class="[
                'inline-flex min-w-0 max-w-full items-center justify-center px-3 py-2 rounded-xl',
                { 'bg-black/10': !isFormStylingActive },
              ]"
            >
              <CompanyLogo
                :nome-empresa="nomeEmpresa"
                :is-dark="isDark"
                :invert="shouldInvertLogo"
                variant="auth"
                @touchstart="startLogoPress"
                @touchend="endLogoPress"
                @touchcancel="endLogoPress"
              />
            </div>
          </div>

          <!-- Mensagens digitando -->
          <div class="space-y-6 mb-8">
            <!-- "Cadastre-se" -->
            <div class="flex justify-center">
              <div
                :class="[
                  'inline-flex px-3 py-1.5 rounded-xl',
                  { 'bg-black/10': !isFormStylingActive },
                ]"
              >
                <h2
                  class="text-2xl sm:text-3xl font-bold text-white text-center"
                >
                  {{ typedText1
                  }}<span
                    v-if="typedText1.length < welcomeMessage1.length"
                    class="animate-pulse"
                    >|</span
                  >
                </h2>
              </div>
            </div>

            <!-- "Preencha seus dados..." -->
            <div v-if="showSecondMessage" class="flex justify-center">
              <div class="inline-flex px-3 py-1 rounded-lg">
                <p class="text-sm text-white/60 text-center">
                  {{ typedText2
                  }}<span
                    v-if="typedText2.length < welcomeMessage2.length"
                    class="animate-pulse"
                    >|</span
                  >
                </p>
              </div>
            </div>
          </div>

          <!-- Formulário (aparece após mensagens) -->
          <div v-if="showFormContent">
            <!-- Voltar -->
            <button
              @click="$router.push('/Login')"
              class="flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-6"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Voltar
            </button>

            <form
              @submit.prevent="
                modoAutenticacao == 2 ? receberToken() : avancar()
              "
              class="space-y-4"
            >
              <!-- Tipo cadastro -->
              <div>
                <label class="block text-sm font-medium mb-2">
                  Tipo de cadastro*
                </label>
                <ModalSelect
                  v-model="cadastro.selectedTipoCliente"
                  :options="tiposClientes"
                  label-key="label"
                  value-key="value"
                  title="Selecionar tipo de cadastro"
                  aria-label="Tipo de cadastro"
                  required
                  placeholder="Selecione um tipo"
                  class="w-full"
                  @change="limparCadastro"
                />
              </div>

              <!-- CNPJ (PJ/FU) -->
              <div v-if="requiresCnpjGate">
                <label class="block text-sm font-medium mb-2"> CNPJ* </label>
                <InputText
                  v-model="cadastro.cnpj"
                  v-mask="cnpjMaskConfig"
                  placeholder="AA.AAA.AAA/AAAA-00"
                  aria-describedby="cnpj-verification-guidance"
                />
                <RecaptchaCheckbox
                  v-if="!registrationDetailsUnlocked"
                  ref="cnpjCaptcha"
                  class="mt-3"
                  @verified="handleCnpjCaptchaVerified"
                  @expired="handleCnpjCaptchaExpired"
                  @error="handleCnpjCaptchaError"
                />
                <p
                  v-if="captchaMessage"
                  class="text-amber-200 text-sm mt-2 font-medium"
                  role="alert"
                >
                  {{ captchaMessage }}
                </p>
                <Button
                  v-if="!registrationDetailsUnlocked"
                  type="button"
                  label="Continuar cadastro"
                  class="w-full mt-3 btn-dynamic-color"
                  :loading="!requisicaoFinalizada"
                  :disabled="!cadastro.cnpj || !requisicaoFinalizada"
                  @click="verifyCNPJ(false)"
                />
                <div
                  v-if="registrationDetailsUnlocked"
                  id="cnpj-verification-guidance"
                  class="cnpj-gate-status cnpj-gate-status--success mt-3"
                  role="status"
                  aria-live="polite"
                >
                  <svg
                    class="cnpj-gate-status__icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="m5 13 4 4L19 7"
                    />
                  </svg>
                  <p>
                    <strong>CNPJ validado.</strong> Continue preenchendo os
                    dados do cadastro.
                  </p>
                </div>
              </div>

              <div
                v-if="requiresCnpjGate && !registrationDetailsUnlocked"
                id="cnpj-verification-guidance"
                class="cnpj-gate-status"
                role="status"
                aria-live="polite"
              >
                <svg
                  class="cnpj-gate-status__icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 15v2m-6 4h12a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2Zm10-10V7a4 4 0 0 0-8 0v4h8Z"
                  />
                </svg>
                <p>
                  Informe o CNPJ, conclua a verificação de segurança e valide
                  para continuar.
                </p>
              </div>

              <div v-if="registrationDetailsUnlocked" class="space-y-4">
                <!-- Nome/Razão Social -->
                <div>
                  <label class="block text-sm font-medium mb-2">
                    {{
                      cadastro.selectedTipoCliente == "2" ||
                      cadastro.selectedTipoCliente == "3" ||
                      cadastro.selectedTipoCliente == "4"
                        ? "Razão Social*"
                        : "Nome completo*"
                    }}
                  </label>
                  <InputText
                    v-model="cadastro.nomeRazaoSocial"
                    :disabled="cadastro.selectedTipoCliente != '1'"
                  />
                </div>

                <!-- CPF (PF) -->
                <div
                  v-if="
                    cadastro.selectedTipoCliente == '1' ||
                    cadastro.selectedTipoCliente == 'selecione'
                  "
                >
                  <label class="block text-sm font-medium mb-2"> CPF* </label>
                  <InputText
                    v-model="cadastro.cpf"
                    v-mask="'###.###.###-##'"
                    placeholder="000.000.000-00"
                    :class="{
                      'p-invalid':
                        cadastro.cpf &&
                        cadastro.cpf.length === 14 &&
                        !validarCPF(cadastro.cpf),
                    }"
                  />
                  <small
                    v-if="
                      cadastro.cpf &&
                      cadastro.cpf.length === 14 &&
                      !validarCPF(cadastro.cpf)
                    "
                    class="text-red-300 text-sm mt-1.5 block font-medium"
                  >
                    ⚠️ CPF inválido
                  </small>
                </div>

                <!-- Data Constituição (PJ/FU) -->
                <div
                  v-if="
                    cadastro.selectedTipoCliente == '2' ||
                    cadastro.selectedTipoCliente == '3' ||
                    cadastro.selectedTipoCliente == '4'
                  "
                >
                  <label class="block text-sm font-medium mb-2">
                    Data de Constituição*
                  </label>
                  <InputText
                    v-model="cadastro.dataConstituicao"
                    v-mask="'##/##/####'"
                    disabled
                  />
                </div>

                <!-- Email -->
                <div>
                  <label class="block text-sm font-medium mb-2">
                    {{ labelEmail }}
                  </label>
                  <InputText
                    v-model="cadastro.email"
                    type="email"
                    :placeholder="placeholderEmail"
                    @blur="verifyEmail"
                  />
                  <p v-if="isApenasPJ" class="mt-2 text-xs text-white/50 block">
                    Este e-mail será utilizado para acessar a conta e receber
                    comunicações.
                  </p>
                </div>

                <!-- Apelido (com config) -->
                <div
                  v-if="
                    configuracaoApelido == 'S' &&
                    cadastro.selectedTipoCliente !== 'selecione' &&
                    cadastro.selectedTipoCliente !== '1'
                  "
                >
                  <div class="flex items-center gap-2 mb-2">
                    <label class="block text-sm font-medium"> Apelido* </label>
                    <i
                      class="fa fa-info-circle info-icon text-white/50"
                      title="O Apelido de Conta permite que você personalize a identificação das suas contas de forma única."
                    ></i>
                  </div>
                  <InputText
                    v-model="cadastro.apelido"
                    placeholder="Insira seu apelido"
                    @input="validarApelido"
                    @blur="checkApelidoLength"
                  />
                  <p v-if="apelidoError" class="erro mt-1">
                    {{ apelidoError }}
                  </p>
                  <p class="text-xs text-white/50 mt-1">
                    O apelido deve ter entre 3 e 20 caracteres, podendo incluir
                    letras e números.
                  </p>
                </div>

                <!-- Assessor (com config) -->
                <div
                  v-if="
                    configuracaoAssessores === 'S' &&
                    modoPreenchimentoAssessor === '1'
                  "
                >
                  <label class="block text-sm font-medium mb-3">
                    Possui ou veio por indicação por {{ nomeCampoAssessor }}?*
                  </label>
                  <div class="flex items-center gap-6 mb-3">
                    <label class="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        v-model="cadastro.assessorPossuiIndicacao"
                        value="N"
                        style="margin-right: 5px; height: 15px"
                        @change="limparAssessor"
                        class="w-4 h-4 accent-white cursor-pointer"
                      />
                      <span class="text-sm">Não</span>
                    </label>
                    <label class="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        v-model="cadastro.assessorPossuiIndicacao"
                        value="S"
                        style="margin-right: 5px; height: 15px"
                        class="w-4 h-4 accent-white cursor-pointer"
                      />
                      <span class="text-sm">Sim</span>
                    </label>
                  </div>

                  <div v-if="cadastro.assessorPossuiIndicacao === 'S'">
                    <label class="block text-sm font-medium mb-2">
                      Se sim, indique o {{ nomeCampoAssessor }} abaixo:
                    </label>
                    <div class="relative">
                      <div class="relative">
                        <input
                          type="text"
                          v-model="assessorBusca"
                          @input="buscarAssessores(assessorBusca)"
                          @blur="fecharDropdownAssessor"
                          :placeholder="`Selecione o ${nomeCampoAssessor}`"
                          autocomplete="off"
                          class="w-full p-inputtext"
                          :class="{ 'pr-8': assessorSelecionadoNome }"
                        />
                        <button
                          v-if="assessorSelecionadoNome"
                          type="button"
                          @mousedown.prevent="limparAssessor"
                          class="absolute right-2 top-1/2 -translate-y-1/2 text-white/60 hover:text-white text-lg leading-none"
                          title="Limpar"
                        >
                          ×
                        </button>
                        <span
                          v-if="assessorBuscando"
                          class="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 text-xs"
                          >...</span
                        >
                      </div>

                      <ul
                        v-if="
                          assessorDropdownAberto && listaAssessores.length > 0
                        "
                        class="assessor-dropdown"
                      >
                        <li
                          v-for="assessor in listaAssessores"
                          :key="assessor.IdExterno"
                          @mousedown.prevent="selecionarAssessor(assessor)"
                          class="assessor-dropdown-item"
                        >
                          <span
                            class="font-medium"
                            style="color: black !important"
                            >{{ assessor.Nome }}</span
                          >
                          <span
                            v-if="assessor.Categoria"
                            class="text-xs opacity-60 ml-2"
                            >{{ assessor.Categoria }}</span
                          >
                        </li>
                      </ul>

                      <p
                        v-if="
                          assessorDropdownAberto &&
                          !assessorBuscando &&
                          listaAssessores.length === 0 &&
                          assessorBusca.length >= 3
                        "
                        class="text-xs text-white/50 mt-1"
                      >
                        Nenhum {{ nomeCampoAssessor }} encontrado.
                      </p>
                      <p
                        v-if="
                          !assessorSelecionadoNome &&
                          assessorBusca.length < 3 &&
                          assessorBusca.length > 0
                        "
                        class="text-xs text-white/50 mt-1"
                      >
                        Digite ao menos 3 caracteres para buscar.
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Botões -->
                <Button
                  v-if="modoAutenticacao != 2"
                  type="submit"
                  label="Avançar"
                  class="w-full btn-dynamic-color"
                />
                <Button
                  v-else
                  type="submit"
                  :loading="disableBtnReceberToken"
                  :disabled="disableBtnReceberToken"
                  label="Receber Código de acesso"
                  class="w-full btn-dynamic-color"
                />
              </div>
            </form>

            <!-- Footer -->
            <div class="mt-6 text-center mb-4">
              <p class="text-sm text-white/60">
                Já possui uma conta?
                <a
                  :href="loginLink"
                  class="font-medium text-white/80 hover:text-white hover:underline transition-colors"
                >
                  Entrar
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <ModalAvisoGenerico
    :mensagem="messageErrorGenerico"
    :mensagensComplementares="mensagensComplementaresGenerico"
  />
</template>

<script>
import { required, email, minLength } from "@vuelidate/validators";
import validateCpf from "@/utils/validateCpf";
import validateCnpj from "@/utils/validateCnpj";
import api from "@/api/axios";
import { mapGetters } from "vuex";
import { mapActions } from "vuex";
import ModalAvisoGenerico from "../Login/ModalAvisoGenerico.vue";
import BackgroundCustomizer from "@/components/BackgroundCustomizer.vue";
import CompanyLogo from "@/components/CompanyLogo.vue";
import RecaptchaCheckbox from "@/components/RecaptchaCheckbox.vue";
import {
  canAccessRegistrationDetails,
  getCnpjVerificationFeedback,
  hasCnpjLookupResult,
  keepVerifiedCnpjForCurrentInput,
  normalizeCnpj,
  requiresCnpjVerification,
} from "@/utils/registrationGate";
import { useTheme } from "@/composables/useTheme";
import { useEditorMode } from "@/composables/useEditorMode";
import ModalSelect from "@/components/ModalSelect.vue";
import { parsePreRegisterChallenge } from "@/utils/preRegisterChallenge";
import { useVisitorData } from "@fingerprint/vue";
import {
  FINGERPRINT_ERROR_MESSAGE,
  getFingerprintEventHeaders,
  getFingerprintEventId,
  isFingerprintEnabled,
} from "@/services/deviceFingerprint";
import { requestCnpjVerification } from "@/services/cnpjVerification";

export default {
  name: "CadastroInicio",
  components: {
    ModalSelect,
    ModalAvisoGenerico,
    BackgroundCustomizer,
    CompanyLogo,
    RecaptchaCheckbox,
  },
  setup() {
    const { isDark, toggleTheme } = useTheme();
    const { editorModeActive, toggleEditorMode, initEditorMode } =
      useEditorMode();
    const getFingerprintData = isFingerprintEnabled()
      ? useVisitorData({ immediate: false }).getData
      : null;

    initEditorMode();

    return {
      isDark,
      toggleTheme,
      editorModeActive,
      toggleEditorMode,
      requestFingerprintEventId: () =>
        getFingerprintEventId(getFingerprintData),
      requestFingerprintEventHeaders: () =>
        getFingerprintEventHeaders(getFingerprintData),
    };
  },
  data() {
    return {
      nomeEmpresa: "",
      challengeId: "",
      messageErrorGenerico: null,
      mensagensComplementaresGenerico: "",
      requisicaoFinalizada: true,
      modoAutenticacao: 0,
      openToken: false,
      messageError: "",
      token: "",
      erro: false,
      disableBtnReceberToken: false,
      disableBtnConcluir: false,
      captchaToken: "",
      captchaMessage: "",
      verifiedCnpj: "",
      cadastro: {
        selectedTipoCliente: "selecione",
        cnpj: null,
        cpf: null,
        nomeRazaoSocial: null,
        email: null,
        apelido: null,
        dataConstituicao: null,
        dadosEndereco: null,
        assessorPossuiIndicacao: null,
        assessorId: null,
      },
      permitirAvancar: false,
      apelidoError: "",
      tiposClientes: [],
      configuracaoTiposClientes: {},
      configuracaoURLEntrar: "",
      configuracaoApelido: "",
      configuracaoAssessores: "",
      modoPreenchimentoAssessor: "",
      assessorBusca: "",
      assessorSelecionadoNome: "",
      listaAssessores: [],
      assessorDropdownAberto: false,
      assessorBuscando: false,
      assessorBuscaTimer: null,
      nomeCampoAssessor: "Assessor",
      loginLink: "/Login",
      // Typing animation
      showTypingWelcome: false,
      typedText1: "",
      typedText2: "",
      welcomeMessage1: "",
      welcomeMessage2: "",
      showSecondMessage: false,
      showFormContent: false,
      customBackgroundStyle: {
        background: "linear-gradient(135deg, #00235C, #003D82, #0056A8)",
      },
      customButtonColor: null,
      isBackgroundReady: false,
      formStylingConfig: null,
      isFormStylingReady: false,
      hasStartedTyping: false,
      // Mobile logo press to reveal customizer
      showBackgroundCustomizer: false,
      logoPressTimer: null,
      // Secret editor mode
      themeClickCount: 0,
      themeClickTimer: null,
      secretInput: "",
      showSecretInput: false,
    };
  },
  computed: {
    ...mapGetters({
      account: "GET_CREATE_ACCOUNT",
      getConfiguracoes: "GET_CONFIGURACOES",
    }),
    cnpjMaskConfig() {
      return {
        mask: "XX.XXX.XXX/XXXX-##",
        tokens: {
          "#": { pattern: /\d/ },
          X: {
            pattern: /[A-Za-z0-9]/,
            transform: (v) => v.toLocaleUpperCase(),
          },
        },
      };
    },
    requiresCnpjGate() {
      return requiresCnpjVerification(this.cadastro.selectedTipoCliente);
    },
    registrationDetailsUnlocked() {
      return canAccessRegistrationDetails(
        this.cadastro.selectedTipoCliente,
        this.cadastro.cnpj,
        this.verifiedCnpj,
      );
    },
    isApenasPJ() {
      // Verifica se apenas PJ está disponível OU se PJ foi selecionado
      const apenasPJDisponivel =
        this.tiposClientes.length === 1 && this.tiposClientes[0].value === "2";
      const pjSelecionado = this.cadastro.selectedTipoCliente === "2";
      return apenasPJDisponivel || pjSelecionado;
    },
    labelEmail() {
      return this.isApenasPJ ? "E-mail da empresa*" : "E-mail*";
    },
    placeholderEmail() {
      return this.isApenasPJ
        ? "Digite o e-mail que será utilizado na conta"
        : "Insira seu e-mail";
    },
    textoDescricaoToken() {
      return this.isApenasPJ
        ? "Para continuar, digite o código que enviamos para o e-mail da empresa, informado no cadastro."
        : "Para continuar, digite o código que acabamos de enviar para o seu e-mail.";
    },
    detectedDevice() {
      const width = window.innerWidth;
      if (width < 768) return "mobile";
      if (width < 1024) return "tablet";
      return "desktop";
    },
    isFormStylingActive() {
      return this.formStylingConfig?.enabled?.[this.detectedDevice] || false;
    },
    currentFormConfig() {
      if (!this.isFormStylingActive) return null;
      return this.formStylingConfig?.[this.detectedDevice] || null;
    },
    formContainerStyle() {
      if (!this.isFormStylingActive || !this.currentFormConfig) return {};
      const { backgroundColor, opacity, width, minWidth, maxWidth, margin } =
        this.currentFormConfig;
      return {
        backgroundColor: this.hexToRgba(backgroundColor, opacity / 100),
        backdropFilter: "blur(16px)",
        borderRadius: "1rem",
        padding: "2rem",
        width: width || "100%",
        minWidth: minWidth || "0px",
        maxWidth: maxWidth || "600px",
        marginLeft: margin || "0rem",
        marginRight: margin || "0rem",
      };
    },
    formContainerClass() {
      if (!this.isFormStylingActive || !this.currentFormConfig) return "";
      const { alignment } = this.currentFormConfig;
      return alignment === "left"
        ? "mr-auto"
        : alignment === "right"
        ? "ml-auto"
        : "mx-auto";
    },
    parentContainerClass() {
      if (!this.isFormStylingActive || !this.currentFormConfig) {
        return "items-center mx-auto";
      }
      const { alignment } = this.currentFormConfig;
      if (alignment === "left") {
        return "items-start";
      } else if (alignment === "right") {
        return "items-end";
      } else {
        return "items-center mx-auto";
      }
    },
    parentContainerStyle() {
      if (!this.isFormStylingActive || !this.currentFormConfig) {
        return {};
      }
      const { alignment, alignmentOffset } = this.currentFormConfig;
      const style = {};

      if (alignment === "left") {
        style.paddingLeft = `${alignmentOffset * 0.25}rem`;
      } else if (alignment === "right") {
        style.paddingRight = `${alignmentOffset * 0.25}rem`;
      }

      return style;
    },
    childContainerWidthClass() {
      if (!this.isFormStylingActive || !this.currentFormConfig) {
        return "w-full";
      }
      return "";
    },
    shouldInvertLogo() {
      if (!this.isFormStylingActive || !this.currentFormConfig) {
        return false;
      }
      return this.currentFormConfig.logoInvert;
    },
    customCadastroTitle() {
      // Se a config ainda não chegou, retorna string vazia para não piscar
      // o default antes do texto customizado ser aplicado.
      if (!this.isFormStylingReady) {
        return "";
      }
      const value = this.currentFormConfig?.customTexts?.cadastroTitle;
      if (!value) {
        return "Cadastre-se";
      }
      if (typeof value === "string") {
        return value;
      }
      if (value.enabled && value.content) {
        return value.content;
      }
      return "Cadastre-se";
    },
    customCadastroSubtitle() {
      if (!this.isFormStylingReady) {
        return "";
      }
      const value = this.currentFormConfig?.customTexts?.cadastroSubtitle;
      if (!value) {
        return "Preencha seus dados para criar sua conta.";
      }
      if (typeof value === "string") {
        return value;
      }
      if (value.enabled && value.content) {
        return value.content;
      }
      return "Preencha seus dados para criar sua conta.";
    },
    dynamicTextStyles() {
      if (!this.isFormStylingActive || !this.currentFormConfig) return "";
      const config = this.currentFormConfig;
      return `
        .form-container {
          --ob-button-hover-bg: ${this.hexToRgba(
            config.buttonColorType1,
            config.buttonOpacityType1 / 100,
          )};
        }
        .form-container {
          background-color: ${this.hexToRgba(
            config.backgroundColor,
            config.opacity / 100,
          )} !important;
          backdrop-filter: blur(16px) !important;
        }
        .form-container h1, .form-container h2, .form-container h3,
        .form-container h4, .form-container h5, .form-container h6,
        .form-container span, .form-container label, .form-container p {
          color: ${config.textColor} !important;
        }
        .form-container input:enabled, .form-container select:enabled, .form-container textarea:enabled {
          background-color: ${config.inputBgEnabled} !important;
          color: ${config.inputTextColor} !important;
        }
        .form-container input:disabled, .form-container select:disabled, .form-container textarea:disabled {
          background-color: ${config.inputBgDisabled} !important;
        }
        .login-glass .form-container label,
        .login-glass .form-container p,
        .login-glass label,
        .login-glass p {
          color: ${config.textColor} !important;
          background: none !important;
          padding: 0 !important;
        }
        .login-glass .form-container .p-inputtext:enabled,
        .login-glass .form-container input.p-inputtext:enabled,
        .login-glass .form-container input.p-component.p-inputtext:enabled,
        .login-glass .form-container .p-password input.p-inputtext:enabled,
        .login-glass .form-container .p-password input.p-component.p-inputtext:enabled,
        .login-glass .form-container .p-dropdown:enabled,
        .login-glass .form-container select:enabled,
        .login-glass .form-container textarea:enabled,
        .login-glass .p-inputtext:enabled,
        .login-glass input.p-inputtext:enabled,
        .login-glass input.p-component.p-inputtext:enabled,
        .login-glass .p-password input.p-inputtext:enabled,
        .login-glass .p-password input.p-component.p-inputtext:enabled,
        .dark .login-glass .p-dropdown,
        .dark .login-glass .p-dropdown.p-component,
        .dark .login-glass .p-select,
        .dark .login-glass .p-select.p-component,
        .dark .login-glass span.p-dropdown.p-component,
        .login-glass .p-dropdown,
        .login-glass .p-dropdown.p-component,
        .login-glass .p-select,
        .login-glass .p-select.p-component {
          background: ${config.inputBgEnabled} !important;
          background-color: ${config.inputBgEnabled} !important;
          color: ${config.inputTextColor} !important;
        }
        .login-glass .form-container .p-inputtext:disabled,
        .login-glass .form-container input.p-inputtext:disabled,
        .login-glass .form-container input.p-component.p-inputtext:disabled,
        .login-glass .form-container .p-password input.p-inputtext:disabled,
        .login-glass .form-container .p-password input.p-component.p-inputtext:disabled,
        .login-glass .form-container .p-dropdown:disabled,
        .login-glass .form-container select:disabled,
        .login-glass .form-container textarea:disabled,
        .login-glass .p-inputtext:disabled,
        .login-glass input.p-inputtext:disabled,
        .login-glass input.p-component.p-inputtext:disabled,
        .login-glass .p-password input.p-inputtext:disabled,
        .login-glass .p-password input.p-component.p-inputtext:disabled {
          background: ${config.inputBgDisabled} !important;
        }
        .login-glass .form-container .p-inputtext,
        .login-glass .form-container input.p-inputtext,
        .login-glass .form-container .p-password input,
        .login-glass .p-inputtext,
        .login-glass input.p-inputtext,
        .login-glass input.p-component.p-inputtext,
        .login-glass .p-password input,
        .login-glass input,
        .login-glass .p-dropdown .p-dropdown-label,
        .login-glass .p-dropdown .p-dropdown-label.p-placeholder,
        .login-glass .p-select .p-select-label,
        .login-glass .p-select .p-select-label.p-placeholder {
          color: ${config.inputTextColor} !important;
        }
        .login-glass .form-container .p-inputtext::placeholder,
        .login-glass .form-container input.p-inputtext::placeholder,
        .login-glass .form-container .p-password input::placeholder,
        .login-glass .p-inputtext::placeholder,
        .login-glass input.p-inputtext::placeholder,
        .login-glass input::placeholder {
          color: ${config.inputTextColor} !important;
          opacity: 0.6 !important;
        }
        .form-container a,
        .form-container ~ div a,
        .form-container ~ .text-center a {
          color: ${config.textColor} !important;
        }
        .form-container button:not([type="submit"]),
        .form-container ~ button:not([type="submit"]),
        button.flex.items-center {
          color: ${config.textColor} !important;
        }
        .form-container button[type="submit"],
        .form-container button.w-full,
        .form-container button.btn-dynamic-color,
        .form-container .p-button:not(.p-button-text):not(.p-button-outlined),
        .form-container .p-button:not(.p-button-text):not(.p-button-outlined) .p-button-label,
        .form-container .p-button:not(.p-button-text):not(.p-button-outlined) span {
          color: ${config.buttonTextColor} !important;
        }
        .form-container button[type="submit"],
        .form-container button.w-full:not(.border),
        .form-container button.btn-dynamic-color,
        .login-glass .p-button:not(.p-button-text):not(.p-button-outlined),
        .login-glass .form-container .p-button:not(.p-button-text):not(.p-button-outlined) {
          background-color: ${this.hexToRgba(
            config.buttonColorType1,
            config.buttonOpacityType1 / 100,
          )} !important;
          border-color: ${this.hexToRgba(
            config.buttonColorType1,
            config.buttonOpacityType1 / 100,
          )} !important;
        }
        .form-container button[type="submit"]:hover,
        .form-container button.w-full:not(.border):hover,
        .form-container button.btn-dynamic-color:hover,
        .login-glass .p-button:not(.p-button-text):not(.p-button-outlined):hover,
        .login-glass .form-container .p-button:not(.p-button-text):not(.p-button-outlined):hover,
        .p-button:not(.p-button-text):not(.p-button-outlined):hover .p-button-label {
          color: ${config.buttonHoverColor} !important;
        }
        button.w-full.border,
        button.h-12.border {
          background-color: ${this.hexToRgba(
            config.buttonColorType2,
            config.buttonOpacityType2 / 100,
          )} !important;
          border-color: ${config.buttonColorType2} !important;
        }
      `;
    },
  },
  validations() {
    const validations = {
      email: { required, email },
    };
    if (this.cadastro.selectedTipoCliente == "1") {
      validations.cpf = {
        required,
        validateCpf,
      };
      validations.nomeRazaoSocial = {
        required,
        minLength: minLength(5),
      };
    } else if (this.cadastro.selectedTipoCliente == "2") {
      validations.cnpj = {
        required,
        validateCnpj,
      };
      validations.nomeRazaoSocial = {
        required,
        minLength: minLength(5),
      };
    }
    return { cadastro: validations };
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.resetCadastro();
    });
  },
  beforeRouteUpdate(to, from, next) {
    this.resetCadastro();
    next();
  },
  async created() {
    await this.fetchConfiguracao([
      "Remover tipo de cadastro",
      "Modo de autenticação",
      "Redirecionamento do botão entrar Onboarding externo",
      "Exibir apelido",
      "Assessores - Ativo",
      "Habilitar campo assessor",
      "Nome do Assessor",
    ]);

    this.modoAutenticacao =
      this.getConfiguracoes("Modo de autenticação")?.Valor ?? 0;

    this.configuracaoTiposClientes = this.getConfiguracoes(
      "Remover tipo de cadastro",
    );
    this.configuracaoURLEntrar = this.getConfiguracoes(
      "Redirecionamento do botão entrar Onboarding externo",
    );
    this.configuracaoApelido =
      this.getConfiguracoes("Exibir apelido")?.Valor ?? "";

    this.configuracaoAssessores =
      this.getConfiguracoes("Assessores - Ativo")?.Valor ?? "";

    this.modoPreenchimentoAssessor =
      this.getConfiguracoes("Habilitar campo assessor")?.Valor ?? "";

    this.nomeCampoAssessor =
      this.getConfiguracoes("Nome do Assessor")?.Valor || "Assessor";

    if (this.configuracaoURLEntrar) {
      this.loginLink = this.configuracaoURLEntrar.Valor;
    }
  },
  mounted() {
    // Verifica se editor mode está ativo
    if (this.editorModeActive) {
      this.showBackgroundCustomizer = true;
    }
    this.nomeEmpresa = process.env.VUE_APP_NOME_EMPRESA;
    if (this.account != null) {
      this.cadastro = this.account;
    }
    if (this.nomeEmpresa == "GENIAL") {
      this.cadastro.selectedTipoCliente = "2";

      // Preencher campos vindos do UserLogin (quando usuário não encontrado)
      if (this.$route.query.email) {
        this.cadastro.email = this.$route.query.email;
      }
      if (this.$route.query.cnpj) {
        this.cadastro.cnpj = this.$route.query.cnpj;
        this.captchaMessage =
          "Conclua a verificação de segurança para consultar o CNPJ.";
      }
    }

    // Preencher tipo de cadastro vindo da query (seleção no UserLogin)
    if (this.$route.query.tipo) {
      this.cadastro.selectedTipoCliente = this.$route.query.tipo;
    }

    // A animação de digitação é iniciada em handleFormStyling()
    // quando o BackgroundCustomizer emite o form-styling-change.
    // Isso evita o flash entre o default e o texto customizado.
    this.$store.commit("SET_MODAL_AVISO_LOGIN", false);
    this.$store.commit("SET_MODAL_AVISO_GENERICO", false);

    // Fallback: se o BackgroundCustomizer não emitir (ex: showControls=false
    // ou falha de load), inicia a animação com o default após 1.5s.
    setTimeout(() => {
      if (!this.hasStartedTyping) {
        this.hasStartedTyping = true;
        this.isFormStylingReady = true;
        this.startTypingAnimation();
      }
    }, 1500);
  },
  methods: {
    ...mapActions({
      signIn: "auth/signIn",
      fetchConfiguracao: "FETCH_CONFIGURACOES",
    }),
    hexToRgba(hex, alpha) {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
    handleFormStyling(config) {
      this.formStylingConfig = config;
      this.isFormStylingReady = true;
      // Dispara a animação após o config chegar, para evitar o flash
      // entre o default e o texto customizado
      if (!this.hasStartedTyping) {
        this.hasStartedTyping = true;
        this.startTypingAnimation();
      }
    },
    validarCPF(cpf) {
      if (!cpf) return false;
      const cpfLimpo = String(cpf).replace(/[\s.-]*/gim, "");

      if (
        cpfLimpo.length !== 11 ||
        cpfLimpo === "00000000000" ||
        cpfLimpo === "11111111111" ||
        cpfLimpo === "22222222222" ||
        cpfLimpo === "33333333333" ||
        cpfLimpo === "44444444444" ||
        cpfLimpo === "55555555555" ||
        cpfLimpo === "66666666666" ||
        cpfLimpo === "77777777777" ||
        cpfLimpo === "88888888888" ||
        cpfLimpo === "99999999999"
      ) {
        return false;
      }

      let soma = 0;
      for (let i = 1; i <= 9; i++) {
        soma += parseInt(cpfLimpo.substring(i - 1, i)) * (11 - i);
      }
      let resto = (soma * 10) % 11;
      if (resto === 10 || resto === 11) resto = 0;
      if (resto !== parseInt(cpfLimpo.substring(9, 10))) return false;

      soma = 0;
      for (let i = 1; i <= 10; i++) {
        soma += parseInt(cpfLimpo.substring(i - 1, i)) * (12 - i);
      }
      resto = (soma * 10) % 11;
      if (resto === 10 || resto === 11) resto = 0;
      if (resto !== parseInt(cpfLimpo.substring(10, 11))) return false;

      return true;
    },
    validarCNPJ(cnpj) {
      if (!cnpj) return false;

      const sanitized = String(cnpj)
        .replace(/[^A-Za-z0-9]/g, "")
        .toUpperCase();
      if (sanitized.length !== 14) return false;
      if (/^([A-Z0-9])\1+$/.test(sanitized)) return false;
      if (!/^\d{2}$/.test(sanitized.substring(12))) return false;

      const valorChar = (ch) => ch.charCodeAt(0) - 48;
      const pesos1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
      const pesos2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

      let soma1 = 0;
      for (let i = 0; i < 12; i++) {
        soma1 += valorChar(sanitized.charAt(i)) * pesos1[i];
      }
      const dv1 = soma1 % 11 < 2 ? 0 : 11 - (soma1 % 11);
      if (dv1 !== parseInt(sanitized.charAt(12), 10)) return false;

      let soma2 = 0;
      for (let j = 0; j < 13; j++) {
        soma2 += valorChar(sanitized.charAt(j)) * pesos2[j];
      }
      const dv2 = soma2 % 11 < 2 ? 0 : 11 - (soma2 % 11);
      return dv2 === parseInt(sanitized.charAt(13), 10);
    },
    handleBackgroundChange(style) {
      this.customBackgroundStyle = style;
      this.isBackgroundReady = true;
    },
    handleButtonColorChange(color) {
      this.customButtonColor = color;
    },
    handleThemeClick() {
      this.toggleTheme();

      // Sincroniza o modo interno do form styling com o tema visual
      if (this.$refs.backgroundCustomizer) {
        const newMode = this.isDark ? "dark" : "light";
        this.$refs.backgroundCustomizer.syncModeWithTheme(newMode);
      }

      this.themeClickCount++;

      if (this.themeClickTimer) {
        clearTimeout(this.themeClickTimer);
      }

      this.themeClickTimer = setTimeout(() => {
        this.themeClickCount = 0;
      }, 2000);

      if (this.themeClickCount === 3) {
        this.showSecretInput = true;
        this.secretInput = "";
        this.themeClickCount = 0;

        this.$nextTick(() => {
          if (this.$refs.secretInput) {
            this.$refs.secretInput.focus();
          }
        });

        setTimeout(() => {
          if (this.secretInput === "") {
            this.showSecretInput = false;
          }
        }, 5000);
      }
    },
    checkSecretCode() {
      const input = this.secretInput.toLowerCase();

      if (input === "editor") {
        this.toggleEditorMode();
        this.showBackgroundCustomizer = this.editorModeActive;
        this.showSecretInput = false;
        this.secretInput = "";
      }

      if (input.length > 6 && input !== "editor") {
        this.showSecretInput = false;
        this.secretInput = "";
      }
    },
    startLogoPress() {
      // Apenas no mobile e para empresas DNWAYS
      if (window.innerWidth >= 1024) {
        // Desktop - sempre mostra
        this.showBackgroundCustomizer = true;
        return;
      }

      const isDnways =
        this.nomeEmpresa === "DNWAYS" ||
        this.nomeEmpresa === "DNWAYS_DEMO_Y" ||
        this.nomeEmpresa === "DNWAYS_DEMO_X";

      if (!isDnways) return;

      // Mobile - requer 5 segundos de press
      this.logoPressTimer = setTimeout(() => {
        this.showBackgroundCustomizer = true;
      }, 5000);
    },
    endLogoPress() {
      if (this.logoPressTimer) {
        clearTimeout(this.logoPressTimer);
        this.logoPressTimer = null;
      }
    },
    startTypingAnimation() {
      this.showTypingWelcome = true;

      // Define as mensagens com textos customizados
      this.welcomeMessage1 = this.customCadastroTitle;
      this.welcomeMessage2 = this.customCadastroSubtitle;

      // Verifica se a animação está desabilitada via env
      const disableTyping = true;

      if (disableTyping) {
        // Mostra tudo imediatamente sem animação
        this.typedText1 = this.customCadastroTitle;
        this.typedText2 = this.customCadastroSubtitle;
        this.showSecondMessage = true;
        this.showFormContent = true;
        return;
      }

      // Animação normal de digitação
      const typingSpeed = 60; // ms por caractere
      const pauseBetweenMessages = 600; // pausa entre mensagens
      const pauseBeforeForm = 500; // pausa antes de mostrar botões

      // Digita primeira mensagem
      let index1 = 0;
      const typeFirstMessage = () => {
        if (index1 < this.customCadastroTitle.length) {
          this.typedText1 += this.customCadastroTitle[index1];
          index1++;
          setTimeout(typeFirstMessage, typingSpeed);
        } else {
          setTimeout(() => {
            this.showSecondMessage = true;
            typeSecondMessage();
          }, pauseBetweenMessages);
        }
      };

      // Digita segunda mensagem
      let index2 = 0;
      const typeSecondMessage = () => {
        if (index2 < this.customCadastroSubtitle.length) {
          this.typedText2 += this.customCadastroSubtitle[index2];
          index2++;
          setTimeout(typeSecondMessage, typingSpeed);
        } else {
          setTimeout(() => {
            this.showFormContent = true;
          }, pauseBeforeForm);
        }
      };

      setTimeout(typeFirstMessage, 300);
    },
    voltar() {
      this.openToken = false;
    },
    clear() {
      this.messageError = "";
      this.erro = false;
    },
    handleCnpjCaptchaVerified(token) {
      this.captchaToken = token;
      this.captchaMessage = "";
    },
    handleCnpjCaptchaExpired() {
      this.captchaToken = "";
      this.captchaMessage =
        "A verificação expirou. Confirme novamente antes de consultar o CNPJ.";
    },
    handleCnpjCaptchaError() {
      this.captchaToken = "";
      this.captchaMessage =
        "Não foi possível concluir a verificação de segurança. Tente novamente.";
    },
    resetCnpjCaptcha() {
      this.captchaToken = "";
      this.$nextTick(() => this.$refs.cnpjCaptcha?.reset());
    },
    apresentarErroVerifyCNPJ(titulo, mensagem) {
      this.messageErrorGenerico =
        titulo ??
        "Não encontramos um registro correspondente ao e-mail ou documento inserido.";
      this.mensagensComplementaresGenerico =
        mensagem ?? "Verifique se digitou corretamente e tente novamente.";

      this.$store.commit("SET_MODAL_AVISO_GENERICO", true);

      this.cadastro = {
        selectedTipoCliente: "2",
        cnpj: null,
        cpf: null,
        nomeRazaoSocial: null,
        email: null,
        apelido: null,
        dataConstituicao: null,
        dadosEndereco: null,
      };
    },
    async verifyCNPJ(silent = false) {
      const cnpjRegex =
        /^[A-Z0-9]{2}\.[A-Z0-9]{3}\.[A-Z0-9]{3}\/[A-Z0-9]{4}-\d{2}$/;
      if (this.cadastro.cnpj == "" || this.cadastro.cnpj == null) {
        return;
      }
      if (
        this.cadastro.cnpj.length > 0 &&
        !cnpjRegex.test(this.cadastro.cnpj.toUpperCase())
      ) {
        if (!silent) {
          this.$alert({
            icon: "error",
            title: "Oops...",
            text: `O CNPJ informado é inválido! Verifique os dados e tente novamente.`,
          });
        }
        return;
      }

      const captchaFeedback = getCnpjVerificationFeedback(
        this.cadastro.cnpj,
        this.captchaToken,
      );
      if (captchaFeedback) {
        this.captchaMessage = captchaFeedback.text;
        if (!silent) {
          this.$alert(captchaFeedback);
        }
        return;
      }

      this.verifiedCnpj = "";
      this.requisicaoFinalizada = false;
      const captchaToken = this.captchaToken;
      this.captchaToken = "";

      try {
        const retorno = await requestCnpjVerification({
          httpClient: api,
          cnpj: this.cadastro.cnpj,
          captchaToken,
          getFingerprintHeaders: this.requestFingerprintEventHeaders,
        });

        if (retorno?.status == 200) {
          if (!hasCnpjLookupResult(retorno.data)) {
            this.apresentarErroVerifyCNPJ();
          } else {
            this.cadastro.nomeRazaoSocial = retorno?.data?.RazaoSocial;
            this.cadastro.dataConstituicao = retorno?.data?.DataConstituicao;
            this.cadastro.dadosEndereco = retorno?.data?.DadosEndereco;
            this.verifiedCnpj = normalizeCnpj(this.cadastro.cnpj);
          }
        } else {
          this.apresentarErroVerifyCNPJ();
        }
      } catch (erro) {
        if (
          erro?.response?.status === 403 ||
          erro?.message === FINGERPRINT_ERROR_MESSAGE
        ) {
          this.captchaMessage =
            "A verificação de segurança não foi aceita. Confirme novamente.";
          if (!silent) {
            this.$alert({
              icon: "warning",
              title: "Verificação necessária",
              text: this.captchaMessage,
            });
          }
        } else {
          this.apresentarErroVerifyCNPJ(
            "Falha na requisição.",
            "A consulta falhou ao localizar CNPJ, tente novamente.",
          );
        }

        console.error(
          "Falha na consulta de CNPJ.",
          erro?.response?.status || "network-error",
        );
      } finally {
        this.requisicaoFinalizada = true;
        this.resetCnpjCaptcha();
      }
    },
    verifyEmail() {
      // Only enforce when "Exibir apelido" is NOT 'S' (i.e., disabled / N)
      if (this.configuracaoApelido === "S") return;

      this.requisicaoFinalizada = false;
      const email = (this.cadastro.email || "").trim();

      // Skip if empty or invalid shape (deeper validation runs in submit)
      if (
        email === "" ||
        !email.includes("@") ||
        email.indexOf("@") === 0 ||
        email.indexOf("@") === email.length - 1
      ) {
        this.requisicaoFinalizada = true;
        return;
      }

      api
        .get(`/register/verifyEmail?email=${encodeURIComponent(email)}`)
        .then((retorno) => {
          this.requisicaoFinalizada = true;
          const body = retorno?.data;
          if (retorno?.status === 200 && body && body.available === false) {
            const mensagem =
              body.message || "Já existe um cadastro para o e-mail informado.";
            this.apresentarErroEmail(mensagem);
          }
        })
        .catch((erro) => {
          this.requisicaoFinalizada = true;
          console.error("Erro no verifyEmail:", erro);
          // Do NOT block the user on a transient backend failure; let submit-time validation handle it.
        });
    },
    apresentarErroEmail(mensagem) {
      this.$alert({
        icon: "error",
        title: "Oops...",
        text: mensagem || "Já existe um cadastro para o e-mail informado.",
      }).then(() => {
        // Clear the email so the user can correct it before advancing
        this.cadastro.email = null;
      });
    },
    limparCadastro() {
      this.verifiedCnpj = "";
      this.captchaToken = "";
      this.captchaMessage = "";
      this.cadastro.razaoSocial = null;
      this.cadastro.cnpj = null;
      this.cadastro.cpf = null;
      this.cadastro.nomeRazaoSocial = null;
      this.cadastro.email = null;
      this.cadastro.apelido = null;
      this.cadastro.dataConstituicao = null;
      this.cadastro.dadosEndereco = null;
      this.cadastro.assessorPossuiIndicacao = null;
      this.cadastro.assessorId = null;
      this.assessorBusca = "";
      this.assessorSelecionadoNome = "";
      this.listaAssessores = [];
      this.challengeId = "";
    },
    resetCadastro() {
      this.verifiedCnpj = "";
      this.captchaToken = "";
      this.captchaMessage = "";
      this.cadastro.selectedTipoCliente = "selecione";
      this.cadastro.razaoSocial = null;
      this.cadastro.cnpj = null;
      this.cadastro.cpf = null;
      this.cadastro.nomeRazaoSocial = null;
      this.cadastro.email = null;
      this.cadastro.apelido = null;
      this.cadastro.dataConstituicao = null;
      this.cadastro.dadosEndereco = null;
      this.cadastro.assessorPossuiIndicacao = null;
      this.cadastro.assessorId = null;
      this.assessorBusca = "";
      this.assessorSelecionadoNome = "";
      this.listaAssessores = [];
      this.challengeId = "";
    },
    validarApelido(event) {
      if (this.configuracaoApelido === "S") {
        const value = event.target.value;
        const regex = /[^a-zA-Z0-9]/g;
        this.cadastro.apelido = value.replace(regex, "").slice(0, 20);
      }
    },
    async buscarAssessores(termo) {
      clearTimeout(this.assessorBuscaTimer);
      this.cadastro.assessorId = null;
      this.assessorSelecionadoNome = "";
      if (!termo || termo.length < 3) {
        this.listaAssessores = [];
        this.assessorDropdownAberto = false;
        return;
      }
      this.assessorBuscaTimer = setTimeout(async () => {
        try {
          this.assessorBuscando = true;
          const res = await api.get(
            `/assessor?nomeAssessor=${encodeURIComponent(termo)}`,
          );
          this.listaAssessores = res.data || [];
          this.assessorDropdownAberto = true;
        } catch {
          this.listaAssessores = [];
        } finally {
          this.assessorBuscando = false;
        }
      }, 400);
    },
    selecionarAssessor(assessor) {
      this.cadastro.assessorId = assessor.IdExterno;
      this.assessorSelecionadoNome = assessor.Nome;
      this.assessorBusca = assessor.Nome;
      this.assessorDropdownAberto = false;
      this.listaAssessores = [];
    },
    limparAssessor() {
      this.cadastro.assessorId = null;
      this.assessorSelecionadoNome = "";
      this.assessorBusca = "";
      this.listaAssessores = [];
      this.assessorDropdownAberto = false;
    },
    fecharDropdownAssessor() {
      setTimeout(() => {
        this.assessorDropdownAberto = false;
      }, 150);
    },
    checkApelidoLength(event) {
      if (!this.cadastro.apelido || this.cadastro.apelido.length < 3) {
        this.apelidoError =
          "O campo apelido deve ter no mínimo três caracteres.";
        event.target.focus();
      } else {
        this.apelidoError = "";
      }
    },
    validarAvancar() {
      var nomeCampo = "";
      if (this.requiresCnpjGate && !this.registrationDetailsUnlocked) {
        this.captchaMessage =
          "Valide o CNPJ e a verificação de segurança para continuar.";
        return false;
      }
      if (
        nomeCampo == "" &&
        (this.cadastro.selectedTipoCliente == "" ||
          this.cadastro.selectedTipoCliente == "selecione")
      ) {
        nomeCampo = "Tipo de cadastro";
      }
      if (
        nomeCampo == "" &&
        this.cadastro.selectedTipoCliente == "1" &&
        (this.cadastro.cpf == "" || this.cadastro.cpf == null)
      ) {
        nomeCampo = "CPF";
      }
      // Valida CPF se preenchido
      if (
        nomeCampo == "" &&
        this.cadastro.selectedTipoCliente == "1" &&
        this.cadastro.cpf &&
        !this.validarCPF(this.cadastro.cpf)
      ) {
        this.$alert({
          icon: "error",
          title: "Oops...",
          text: "O CPF informado é inválido. Verifique os dados e tente novamente.",
        });
        return false;
      }
      if (
        nomeCampo == "" &&
        (this.cadastro.selectedTipoCliente == "2" ||
          this.cadastro.selectedTipoCliente == "3" ||
          this.cadastro.selectedTipoCliente == "4") &&
        (this.cadastro.cnpj == "" || this.cadastro.cnpj == null)
      ) {
        nomeCampo = "CNPJ";
      }
      // Valida CNPJ se preenchido
      if (
        nomeCampo == "" &&
        (this.cadastro.selectedTipoCliente == "2" ||
          this.cadastro.selectedTipoCliente == "3" ||
          this.cadastro.selectedTipoCliente == "4") &&
        this.cadastro.cnpj &&
        !this.validarCNPJ(this.cadastro.cnpj)
      ) {
        this.$alert({
          icon: "error",
          title: "Oops...",
          text: "O CNPJ informado é inválido. Verifique os dados e tente novamente.",
        });
        return false;
      }
      if (
        nomeCampo == "" &&
        this.cadastro.selectedTipoCliente == "1" &&
        (this.cadastro.nomeRazaoSocial == "" ||
          this.cadastro.nomeRazaoSocial == null)
      ) {
        nomeCampo = "Nome completo";
      }
      if (
        nomeCampo == "" &&
        (this.cadastro.selectedTipoCliente == "2" ||
          this.cadastro.selectedTipoCliente == "3" ||
          this.cadastro.selectedTipoCliente == "4") &&
        (this.cadastro.nomeRazaoSocial == "" ||
          this.cadastro.nomeRazaoSocial == null)
      ) {
        nomeCampo = "Razão Social";
      }
      if (
        nomeCampo == "" &&
        (this.cadastro.email == "" || this.cadastro.email == null)
      ) {
        nomeCampo = "E-mail";
      }
      if (
        nomeCampo == "" &&
        this.configuracaoApelido === "S" &&
        this.cadastro.selectedTipoCliente != "1" &&
        (this.cadastro.apelido == "" || this.cadastro.apelido == null)
      ) {
        nomeCampo = "Apelido";
      }
      if (
        nomeCampo == "" &&
        this.configuracaoAssessores === "S" &&
        this.modoPreenchimentoAssessor === "1" &&
        !this.cadastro.assessorPossuiIndicacao
      ) {
        nomeCampo = "Indicação por assessor (Sim ou Não)";
      }
      if (
        nomeCampo == "" &&
        this.configuracaoAssessores === "S" &&
        this.modoPreenchimentoAssessor === "1" &&
        this.cadastro.assessorPossuiIndicacao === "S" &&
        !this.cadastro.assessorId
      ) {
        nomeCampo = "Assessor";
      }
      if (nomeCampo != "") {
        this.$alert({
          icon: "error",
          title: "Oops...",
          text: `É necessário preencher o campo ` + nomeCampo + `.`,
        });
        return false;
      }
      // Para PF, preencher apelido automaticamente com primeiro e ultimo nome
      if (
        this.cadastro.selectedTipoCliente == "1" &&
        this.configuracaoApelido == "S"
      ) {
        const nomeCompleto = this.cadastro.nomeRazaoSocial || "";
        const nomes = nomeCompleto.trim().split(" ");
        if (nomes.length >= 2) {
          this.cadastro.apelido = nomes[0] + " " + nomes[nomes.length - 1];
        } else if (nomes.length === 1) {
          this.cadastro.apelido = nomes[0];
        }
      }
      if (
        this.configuracaoApelido === "S" &&
        this.cadastro.selectedTipoCliente !== "1"
      ) {
        this.validarApelido({ target: { value: this.cadastro.apelido } });
        if (this.apelidoError) {
          return false;
        }
      }
      return true;
    },
    avancar() {
      if (!this.validarAvancar()) {
        return;
      } else {
        this.$store.commit("SET_CREATE_ACCOUNT", this.cadastro);
        this.$router.push({ name: "CadastroFinalizacao" });
      }
    },
    async sendRegisterWithToken() {
      this.disableBtnConcluir = true;
      if (!this.token || !this.challengeId) {
        this.$alert({
          icon: "error",
          title: "Oops...",
          text: `Código de verificação inválido ou expirado. Solicite um novo código.`,
        });
        this.disableBtnConcluir = false;
        return;
      }
      if (this.validarAvancar()) {
        let fingerprintEventId = null;
        if (this.cadastro.selectedTipoCliente === "2") {
          try {
            fingerprintEventId = await this.requestFingerprintEventId();
          } catch (error) {
            console.error("Falha na identificação segura do dispositivo.", error);
            this.$alert({
              icon: "error",
              title: "Verificação de segurança",
              text: "Não foi possível validar a segurança deste dispositivo. Atualize a página e tente novamente.",
            });
            this.disableBtnConcluir = false;
            return;
          }
        }

        var registerObj = {
          TipoCadastro: this.cadastro.selectedTipoCliente,
          CpfCnpj:
            this.cadastro.selectedTipoCliente == "1"
              ? this.cadastro.cpf
              : this.cadastro.cnpj,
          NomeRazaoSocial: this.cadastro.nomeRazaoSocial,
          Email: this.cadastro.email,
          DataConstituicao: this.cadastro.dataConstituicao,
          DadosEndereco: this.cadastro.dadosEndereco,
          Token: this.token,
          ChallengeId: this.challengeId,
          Apelido: this.cadastro.apelido,
          AssessorId: this.cadastro.assessorId,
          AssessorPossuiIndicacao:
            this.cadastro.assessorPossuiIndicacao == "S"
              ? true
              : this.cadastro.assessorPossuiIndicacao == "N"
              ? false
              : null,
          // Nome legado do contrato HTTP; o valor é o event_id do agente v4.
          FingerprintRequestId: fingerprintEventId,
        };
        try {
          const retorno = await api.post(
            "/register/registerWithToken",
            registerObj,
          );
          this.disableBtnConcluir = false;
          if (
            !typeof retorno.data[0] === "string" &&
            retorno.data[0].includes("Token")
          ) {
            this.$alert({
              icon: "error",
              title: "Oops...",
              text: `Senha informada não atende aos critérios. É necessário possuir o mínimo de 8 caracteres, sendo ao menos 1 letra maiúscula, 1 minúscula e 1 caractere especial.`,
            });
          } else {
            this.challengeId = "";
            this.disableBtnConcluir = false;
            this.$store.commit("SET_CREATE_ACCOUNT", []);

            const model = {
              Usuario: this.cadastro.email,
              Token: this.token,
              Apelido: this.cadastro.apelido,
              TipoCliente: this.cadastro.selectedTipoCliente,
            };

            await this.signIn(model)
              .then((response) => {
                this.limparCadastro();
                this.disableBtnLogin = false;
                if (response.status == 200) {
                  this.messageError = "";
                  this.erro = false;

                  if (this.cadastro.selectedTipoCliente == "1") {
                    this.$router.push({ name: "cadastro-pf" });
                  } else if (this.cadastro.selectedTipoCliente == "2") {
                    this.$router.push({ name: "cadastro-pj" });
                  } else if (this.cadastro.selectedTipoCliente == "3") {
                    this.$router.push({ name: "cadastro-fi" });
                  }
                }
              })
              .catch((error) => {
                this.limparCadastro();
                this.disableBtnLogin = false;
                if (error.response.data == "") {
                  this.messageError =
                    "Falha na autenticação. Entre em contato com o suporte.";
                  this.erro = true;
                } else if (
                  error.response.data?.data[0] ==
                  "Cliente não validado o e-mail!"
                ) {
                  this.messageError =
                    "Falha na autenticação. Confirme seu endereço de e-mail.";
                  this.erro = true;
                  this.habilitarReenviarEmail = true;
                } else {
                  this.messageError = error.response.data?.data[0];
                  this.erro = true;
                }
              });
          }
        } catch (error) {
          console.error("Erro ao criar a conta:", error);

          const descricao = error?.response?.data?.descricao || "";
          const iconType = descricao.includes("Esse e-mail já foi cadastrado")
            ? "warning"
            : "error";

          this.$alert({
            icon: iconType,
            title: iconType == "warning" ? "Atenção!" : "Erro",
            text:
              error?.response?.data?.descricao != ""
                ? error?.response?.data?.descricao
                : "Dados inválidos",
          });
        } finally {
          this.disableBtnConcluir = false;
        }
      } else {
        this.disableBtnReceberToken = false;
      }
    },
    async receberToken() {
      this.disableBtnReceberToken = true;
      if (this.validarAvancar()) {
        let fingerprintEventId = null;
        if (this.cadastro.selectedTipoCliente === "2") {
          try {
            fingerprintEventId = await this.requestFingerprintEventId();
          } catch (error) {
            console.error("Falha na identificação segura do dispositivo.", error);
            this.$alert({
              icon: "error",
              title: "Verificação de segurança",
              text: "Não foi possível validar a segurança deste dispositivo. Atualize a página e tente novamente.",
            });
            this.disableBtnReceberToken = false;
            return;
          }
        }

        const preRegisterPayload = {
          ...this.cadastro,
          // Nome legado do contrato HTTP; o valor é o event_id do agente v4.
          FingerprintRequestId: fingerprintEventId,
        };
        await api
          .post("/register/sendEmailPreRegister", preRegisterPayload)
          .then((retorno) => {
            const challenge = parsePreRegisterChallenge(retorno.data);
            this.challengeId = challenge.challengeId;
            this.openToken = true;
            this.disableBtnReceberToken = false;
          })
          .catch((error) => {
            this.$alert({
              icon: "error",
              title: "Erro",
              text:
                error?.response?.data?.data != ""
                  ? error?.response?.data?.data
                  : "Dados inválidos",
            }).then(() => {
              this.$router.push({ name: "UserLogin" });
            });
          });
        return;
      } else {
        this.disableBtnReceberToken = false;
      }
    },
  },
  watch: {
    "cadastro.cnpj"(newCnpj) {
      const previouslyVerifiedCnpj = this.verifiedCnpj;
      this.verifiedCnpj = keepVerifiedCnpjForCurrentInput(
        newCnpj,
        this.verifiedCnpj,
      );

      if (previouslyVerifiedCnpj && !this.verifiedCnpj) {
        this.cadastro.nomeRazaoSocial = null;
        this.cadastro.dataConstituicao = null;
        this.cadastro.dadosEndereco = null;
        this.captchaToken = "";
        this.captchaMessage =
          "O CNPJ foi alterado. Faça uma nova verificação para continuar.";
        this.$nextTick(() => this.$refs.cnpjCaptcha?.reset());
      }
    },
    customCadastroTitle(newVal) {
      this.welcomeMessage1 = newVal;
      this.typedText1 = newVal;
    },
    customCadastroSubtitle(newVal) {
      this.welcomeMessage2 = newVal;
      // Se a animação está desabilitada, atualiza o texto diretamente
      this.typedText2 = newVal;
    },
    consultarPermissaoAvancar() {
      this.permitirAvancar = false;
      if (this.cadastro.selectedTipoCliente == "1") {
        if (
          this.cadastro.nomeCompleto &&
          this.cadastro.nomeCompleto.length > 5 &&
          this.validarCPF(this.cadastro.CPF)
        ) {
          this.permitirAvancar = true;
        }
      } else if (
        this.cadastro.selectedTipoCliente == "2" ||
        this.cadastro.selectedTipoCliente == "3" ||
        this.cadastro.selectedTipoCliente == "4"
      ) {
        if (
          this.cadastro.razaoSocial &&
          this.cadastro.CNPJ &&
          this.validarCNPJ(this.cadastro.CNPJ)
        ) {
          this.permitirAvancar = true;
        }
      }
    },
    configuracaoTiposClientes(newVal) {
      if (newVal && newVal.Valor) {
        // O backend retorna múltiplos valores separados por CRLF (Windows)
        // ou vírgula. String.replace com string só troca a 1ª ocorrência,
        // por isso usamos regex com flag 'g' (ou split direto).
        const valores = newVal.Valor.split(/[\r\n,;]+/)
          .map((item) => item.trim())
          .filter((item) => item);
        const todasOpcoes = [
          { value: "1", label: "Pessoa Física" },
          { value: "2", label: "Pessoa Jurídica" },
          { value: "3", label: "Fundo de Investimento" },
          { value: "4", label: "Institucional" },
        ];
        const tiposFiltrados = todasOpcoes.filter(
          (opcao) => !valores.includes(opcao.value),
        );
        if (
          JSON.stringify(tiposFiltrados) !== JSON.stringify(this.tiposClientes)
        ) {
          this.tiposClientes = tiposFiltrados;
        }
      } else {
        if (this.tiposClientes.length > 0) {
          this.tiposClientes = [];
        }
      }
    },
  },
};
</script>

<style scoped>
.cnpj-gate-status {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.24);
  border-radius: 0.75rem;
  background: rgba(15, 23, 42, 0.28);
  color: rgba(255, 255, 255, 0.88);
  font-size: 0.875rem;
  line-height: 1.45;
}

.cnpj-gate-status--success {
  border-color: rgba(134, 239, 172, 0.48);
  background: rgba(22, 101, 52, 0.24);
  color: rgb(220, 252, 231);
}

.cnpj-gate-status__icon {
  width: 1.25rem;
  height: 1.25rem;
  flex: 0 0 auto;
  margin-top: 0.0625rem;
}

.assessor-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 50;
  margin-top: 2px;
  max-height: 220px;
  overflow-y: auto;
  border-radius: 0.5rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  list-style: none;
  margin-left: 0;
  padding: 0.25rem 0;
}

.assessor-dropdown li {
  color: rgba(0, 0, 0, 0.9) !important;
}

.assessor-dropdown-item {
  padding: 0.6rem 0.875rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.9) !important;
  transition: background 0.15s;
  display: flex;
  align-items: baseline;
}

.assessor-dropdown-item:hover {
  background: rgba(255, 255, 255, 0.12);
}
</style>
